(ns app.captions.words
  "The transcript words: the words of the transcription response as the maps
   that become the `:word/*` entities of a media asset (times relative
   to the media), the words a captions clip shows of them, and the
   `words` variable (JSON, seconds) that the caption compositions read.
   Shared by the transcribe command, the renderer step and the editor."
  (:require [clojure.string :as str]
            #?(:clj [cheshire.core :as json])))

(defn seconds->ms
  [seconds]
  (long (Math/round (* 1000.0 (double seconds)))))

(defn normalize-word
  "A word of the transcription response (`{:word :start :end}`, seconds;
   some Whisper providers put a blank in front of the word)."
  [{:keys [word start end]}]
  (when (and (string? word)
             (seq (str/trim word))
             (number? start)
             (number? end))
    {:word/text (str/trim word)
     :word/start (seconds->ms start)
     :word/end (seconds->ms end)}))

(defn clamp
  [ms duration-ms]
  (max 0 (min ms duration-ms)))

(defn monotonic
  "The words in the order of the response with times that never run
   backwards. Whisper lists the words in speech order, but a word can
   start before the word before it ends (\"of the day I\" came with
   an \"I\" that began 0.4 s early); sorted by time such words would
   swap places, so the order wins and the early start moves up to the
   end of the word before. A word is kept at least a millisecond long."
  [words]
  (reduce (fn [words word]
            (let [start (max (:word/start word)
                             (:word/end (peek words) 0))]
              (conj words
                    (assoc word
                           :word/start start
                           :word/end (max (:word/end word)
                                          (inc start))))))
          []
          words))

(defn normalize
  "The valid words in speech order within `[0 duration-ms]`, each with
   its `:word/index`. Words without a positive length are dropped."
  [raw-words duration-ms]
  (->> raw-words
       (keep normalize-word)
       (map (fn [word]
              (-> word
                  (update :word/start clamp duration-ms)
                  (update :word/end clamp duration-ms))))
       (filter (fn [{:word/keys [start end]}]
                 (< start end)))
       (monotonic)
       (map-indexed (fn [index word]
                      (assoc word :word/index index)))
       (vec)))

(defn response-words
  "The raw words of a transcription response body (`verbose_json` with
   word timestamps), or nil when it has none. No speech is an empty
   list."
  [body]
  (let [raw (:words body)]
    (when (sequential? raw)
      raw)))

(def word-attributes
  [:word/uuid :word/text :word/start :word/end :word/index
   :word/emphasis? :word/break? :word/emoji :word/color])

(defn word-map
  "A word (entity or map) as a plain map."
  [word]
  (into {}
        (keep (fn [attribute]
                (when-some [value (get word attribute)]
                  [attribute value])))
        word-attributes))

(defn media-relative
  "The words of a transcribed section with their times relative to the
   media, which is how the transcript is stored: on the media asset, so
   every clip of it on every timeline can show its own range."
  [words offset-ms]
  (mapv (fn [word]
          (-> word
              (update :word/start + offset-ms)
              (update :word/end + offset-ms)))
        words))

(defn in-range?
  "A word belongs to the range that contains its middle, so a word at a
   cut shows up on exactly one side of it."
  [{:keys [offset-ms duration-ms]} word]
  (let [middle (/ (+ (:word/start word) (:word/end word)) 2)]
    (and (<= offset-ms middle)
         (< middle (+ offset-ms duration-ms)))))

(defn clip-words
  "The words a captions clip shows: the words of the media that overlap
   the clip's range `{:offset-ms :duration-ms}` (the clip's offset into
   the media and its length), with times relative to the clip."
  [words {:keys [offset-ms duration-ms] :as clip-range}]
  (->> words
       (map word-map)
       (filter (partial in-range? clip-range))
       (map (fn [word]
              (-> word
                  (update :word/start (fn [start]
                                        (max 0 (- start offset-ms))))
                  (update :word/end (fn [end]
                                      (min duration-ms (- end offset-ms)))))))
       (sort-by (juxt :word/start :word/index))
       (vec)))

(defn composition-word
  "One word as the caption compositions read it: seconds plus the
   per-word styling."
  [word]
  (cond-> {:text (:word/text word)
           :start (/ (:word/start word) 1000.0)
           :end (/ (:word/end word) 1000.0)}
    (:word/emphasis? word) (assoc :emphasis true)
    (:word/break? word) (assoc :break true)
    (:word/emoji word) (assoc :emoji (:word/emoji word))
    (:word/color word) (assoc :color (:word/color word))))

(defn composition-words
  [words]
  (->> words
       (sort-by (juxt :word/start :word/index))
       (mapv composition-word)))

(defn composition-json
  "The `words` variable of a caption composition, from words with
   times relative to the clip (see `clip-words`)."
  [words]
  #?(:clj (json/generate-string (composition-words words))
     :cljs (js/JSON.stringify (clj->js (composition-words words)))))

(comment
  (normalize [{:word "Hello" :start 0.1 :end 0.4}
              {:word " world " :start 0.4 :end 0.9}
              {:word "" :start 1 :end 2}]
             5000)
  )
